import { useMemo } from 'react';
import parse, { Element, Text } from 'html-react-parser';
import type { DOMNode } from 'html-react-parser';

// Characters of text a card shows until its reader asks for the rest, counted without markup.
const PREVIEW_LENGTH = 500;

// A cut inside a character drawn from several code points, such as an emoji with its skin tone, would change it.
const textBefore = (text: string, length: number) => {
  // Firefox before 125 has no Intl.Segmenter; a surrogate pair is the most it can keep whole.
  if (typeof Intl.Segmenter !== 'function') {
    return text.substring(0, /[\uD800-\uDBFF]/.test(text[length - 1] ?? '') ? length - 1 : length);
  }
  let end = 0;
  for (const { index, segment } of new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)) {
    if (index + segment.length > length) break;
    end = index + segment.length;
  }
  // A first character longer than the cut, such as a letter under hundreds of accents, is cut inside, not dropped.
  return text.substring(0, end || length);
};

// A table draws none of the white space between its parts, and html-react-parser leaves it out as well.
const TABLE_PARTS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'colgroup']);

const isTableWhiteSpace = (text: string, parentName = '') => TABLE_PARTS.has(parentName) && !text.trim();

// The text a card draws: under `.ql-editor`'s pre-wrap that is every character, bar the white space a table drops.
const drawnTextOf = (content: DocumentFragment): string => {
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  let text = '';
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const { data, parentNode } = node as CharacterData;
    if (!isTableWhiteSpace(data, parentNode?.nodeName.toLowerCase())) text += data;
  }
  return text;
};

// Keeps the first `length` characters of the text and cuts between tags, so no element is left half written.
const previewOf = (length: number) => {
  let left = length;
  // White space between blocks cannot hold the mark, so the next text that shows takes it.
  let isMarkPending = false;
  // Named, as react/display-name reads an unnamed function that returns JSX as a component without a name.
  const replace = (node: DOMNode) => {
    if (!(node instanceof Text)) {
      if (left > 0) return undefined;
      return isMarkPending && node instanceof Element && node.children.length > 0 ? undefined : <></>;
    }
    if (isTableWhiteSpace(node.data, node.parent instanceof Element ? node.parent.name : undefined)) return undefined;
    if (left === 0) {
      if (!isMarkPending || !/\S/.test(node.data)) return <></>;
      isMarkPending = false;
      return <span>...</span>;
    }
    if (node.data.length < left) {
      left -= node.data.length;
      return undefined;
    }
    const kept = node.data.substring(0, left);
    left = 0;
    if (!/\S/.test(node.data)) {
      isMarkPending = true;
      return <></>;
    }
    return (
      <>
        {kept}
        <span>...</span>
      </>
    );
  };
  return replace;
};

// A template's content is inert, so reading the HTML this way loads none of its images.
const inertContent = (html: string): DocumentFragment => {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
};

// Each of these starts a new line, so its text would otherwise run into the text beside it.
const LINE_BREAKS = 'p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre, br';

/** The text of sanitized HTML on one line, cut after `length` characters with a mark, for a one-line preview. */
export const htmlTextPreview = (html: string, length: number): string => {
  const content = inertContent(html);
  content.querySelectorAll(LINE_BREAKS).forEach((node) => node.after(' '));
  const text = (content.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text.length > length ? `${textBefore(text, length)}...` : text;
};

/**
 * HTML a user wrote, as a card shows it: sanitized, and cut after 500 characters of text until `expanded`. It is
 * worked out once per text and state, as a pasted screenshot is stored in the text and makes every pass slow.
 */
export const useHtmlPreview = (text: string, sanitize: (html: string) => string, expanded: boolean) => {
  const { html, isLong, cut } = useMemo(() => {
    const sanitized = sanitize(text);
    const drawn = drawnTextOf(inertContent(sanitized));
    // The cut is picked on the whole text, as one character's code points can sit in neighbouring text nodes.
    return { html: sanitized, isLong: drawn.length > PREVIEW_LENGTH, cut: textBefore(drawn, PREVIEW_LENGTH).length };
  }, [text, sanitize]);
  const content = useMemo(
    () => parse(html, isLong && !expanded ? { replace: previewOf(cut) } : undefined),
    [html, isLong, cut, expanded]
  );
  return { html, isLong, content };
};
